'use client';

import InfoIcon from '@mui/icons-material/Info';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid2 as Grid,
	MenuItem,
	Select,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { gql, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import {
	AutoInvoiceSettingEnum,
	AutoInvoiceSettingForPrismaEnum,
	RecurringPaymentsSettingDisplayEnum,
} from 'parkchirp-shared/enums';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../contexts';
import {
	CREATE_SUBSCRIPTION_INVOICE_TEMPLATES,
	GET_ALL_INVOICE_TEMPLATES,
	GET_SUBSCRIPTION_INVOICE_TEMPLATES,
	GET_SUBSCRIPTION_SETTINGS,
	GET_USER_PAYMENT_METHODS,
	LIST_MANUAL_INVOICE_BILLING_SETTINGS,
	UPDATE_SUBSCRIPTION_SETTINGS,
} from '../../queries';
import { InvoiceTemplateSelection, UserPaymentMethods } from '../../types';
import { isOwnerPortal, isTenantPortal } from '../../utils';
import { InvoiceTemplateSelections } from '../invoices/invoiceTemplateSelections';
import { SubscriptionAutoAccessControl } from './subscriptionAutoAccessControl';
import { SubscriptionSettingsRow, SubscriptionSettingsSection } from './subscriptionSettingsLayout';

export const GET_FACILITY_SETTINGS = gql`
    query getFacilitySettings($facility_id: String!) {
        getFacilitySettings(facility_id: $facility_id) {
            use_ach
			use_credit_card
        }
    }
`;

const BATCH_INVOICE_DAY_OPTIONS = Array.from({ length: 28 }, (_, idx) => idx + 1);

const getOrdinalSuffix = (day: number) => {
	if (day > 3 && day < 21) {
		return `${day}th`;
	}

	switch (day % 10) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
};

type SubscriptionSettingsProps = {
	showAutoAccessControls?: boolean;
	subscriptionId: string;
	facilityId: string;
	loading: boolean;
	setShowSnackbar: any;
	cognitoSub: string;
	paymentToken: string;
	refetch: any;
};

type ManualInvoiceBillingSetting = {
	id: number | null;
	profileName: string;
	billingDayInArrears: number;
	dueDayInArrears: number;
	billingDay: number;
	dueDay: number;
	facilityId?: string;
	hasError?: boolean;
};

export const SubscriptionSettings: FC<SubscriptionSettingsProps> = ({
	showAutoAccessControls = false,
	subscriptionId,
	facilityId,
	loading,
	cognitoSub,
	setShowSnackbar,
	paymentToken,
	refetch,
}) => {
	const [allowACHPayments, setAllowACHPayments] = useState<boolean>(true);
	const [pageReady, setPageReady] = useState<boolean>(false);
	const [editMode, setEditMode] = useState<boolean>(false);
	const [autoApplyCredits, setAutoApplyCredits] = useState<boolean>(true);
	const [autoAccessEnabled, setAutoAccessEnabled] = useState<boolean>(true);
	const [allowCreditCardPayments, setAllowCreditCardPayments] = useState<boolean>(true);
	const [autoInvoiceSetting, setAutoInvoiceSetting] = useState<string>('INVOICE_NOW');
	const [batchNewParkers, setBatchNewParkers] = useState<boolean>(false);
	const [sendBatchInvoiceOn, setSendBatchInvoiceOn] = useState<number | ''>('');
	const [recurringPaymentsSetting, setRecurringPaymentsSetting] = useState('ALL_INVOICES');
	const [facilityAllowsACHPayments, setFacilityAllowsACHPayments] = useState<boolean>(true);
	const [facilityAllowsCreditCardPayments, setFacilityAllowsCreditCardPayments] = useState<boolean>(true);
	const [autoPay, setAutoPay] = useState<boolean>(false);
	const [creditCardFees, setCreditCardFees] = useState<boolean>(true);
	const [lateFees, setLateFees] = useState<boolean>(true);
	const [mailInvoiceFees, setMailInvoiceFees] = useState<boolean>(true);
	const [originalValues, setOriginalValues] = useState<any>({});
	const [transactionFees, setTransactionFees] = useState<boolean>(true);
	const [vehicleSurchargeFees, setVehicleSurchargeFees] = useState<boolean>(true);
	const [invoiceOption, setInvoiceOption] = useState<string>('Invoice Now');
	const [manualInvoiceDueDateOffset, setManualInvoiceDueDateOffset] = useState(30);
	const [numberOfParkingSpaces, setNumberOfParkingSpaces] = useState<number | null>(null);
	const [tibaMType, setTibaMType] = useState<number | null>(null);
	const [poolSizeActive, setPoolSizeActive] = useState<boolean | null>(null);
	const [poolSizeLimit, setPoolSizeLimit] = useState<number | null>(null);
	const [integration, setIntegration] = useState<string | null>(null);
	const [unpaidInvoiceReminder, setUnpaidInvoiceReminder] = useState<boolean>(false);
	// Invoice Format Options
	const [monthlySelection, setMonthlySelection] = useState<InvoiceTemplateSelection>(null);
	const [carryoverSelection, setCarryoverSelection] = useState<InvoiceTemplateSelection>(null);
	const [validationSelection, setValidationSelection] = useState<InvoiceTemplateSelection>(null);
	const [hotelStorageSelection, setHotelStorageSelection] = useState<InvoiceTemplateSelection>(null);
	const [officeRetailSelection, setOfficeRetailSelection] = useState<InvoiceTemplateSelection>(null);
	const [miscSelection, setMiscSelection] = useState<InvoiceTemplateSelection>(null);
	const [invoiceFormatDataLoaded, setInvoiceFormatDataLoaded] = useState<boolean>(false);

	const [showACHPaymentTooltip, setShowACHPaymentTooltip] = useState<boolean>(false);
	const [showCreditCardPaymentTooltip, setShowCreditCardPaymentTooltip] = useState<boolean>(false);
	const { AccessControlledComponents, hasComponentAccess } = useContext(CommonContext);
	const canEditSubscriptionSettings =
		!AccessControlledComponents?.SubscriptionInfoSettingsEditButton ||
		hasComponentAccess(AccessControlledComponents.SubscriptionInfoSettingsEditButton);

	const { data: subscriptionInvoiceTemplateData, loading: loadingSubscriptionInvoiceTemplates } = useQuery(
		GET_SUBSCRIPTION_INVOICE_TEMPLATES,
		{
			variables: {
				subscriptionId,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
			skip: !subscriptionId,
		},
	);

	const { data: allInvoiceTemplates, loading: loadingAllInvoiceTemplates } = useQuery(GET_ALL_INVOICE_TEMPLATES, {
		variables: {},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	useQuery(GET_FACILITY_SETTINGS, {
		variables: {
			facility_id: facilityId,
		},
		context: {
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			if (data?.getFacilitySettings) {
				setFacilityAllowsACHPayments(data.getFacilitySettings.use_ach);
				setFacilityAllowsCreditCardPayments(data.getFacilitySettings.use_credit_card);
			}
		},
		skip: !facilityId,
	});

	const [createSubscriptionInvoiceTemplates, { loading: createSubscriptionInvoiceInProgress }] = useMutation(
		CREATE_SUBSCRIPTION_INVOICE_TEMPLATES,
		{
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
		},
	);

	const [selectedBillingSetting, setSelectedBillingSetting] = useState<any>({
		id: null,
		profileName: 'Invoice Now',
		billingDayInArrears: 0,
		dueDayInArrears: 0,
		dueDay: 0,
		billingDay: 0,
	});
	const [loadedBillingSettings, setLoadedBillingSettings] = useState<boolean>(false);

	const [billingSettings, setBillingSettings] = useState<ManualInvoiceBillingSetting[]>([
		{
			id: null,
			profileName: 'Invoice Now',
			billingDayInArrears: 0,
			dueDayInArrears: 0,
			dueDay: 0,
			billingDay: 0,
		},
	]);

	const {
		data,
		error: queryError,
		loading: queryLoading,
	} = useQuery(GET_SUBSCRIPTION_SETTINGS, {
		variables: { subscriptionId, includeAutoAccessControls: showAutoAccessControls },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		skip: !subscriptionId,
	});

	const facilityAutoAccessEnabled =
		data?.getSubscriptionById?.billingProfile?.facility?.facilitySettings?.autoAccessEnabled === true;

	const { data: facilityInvoiceBillingProfiles } = useQuery(LIST_MANUAL_INVOICE_BILLING_SETTINGS, {
		variables: { facilityId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		skip: !facilityId,
	});

	const [getUserPaymentMethods, { data: paymentMethodsData }] =
		useLazyQuery<UserPaymentMethods>(GET_USER_PAYMENT_METHODS);

	useEffect(() => {
		if (cognitoSub) {
			getUserPaymentMethods({
				variables: { userId: cognitoSub },
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				fetchPolicy: 'network-only',
			});
			refetch();
		}
	}, [cognitoSub, getUserPaymentMethods, refetch]);

	const isBankAccountSelectedForRecurringPayments: boolean =
		paymentMethodsData?.getUserPaymentMethods?.gpiBankAccounts?.some((account) => account.token === paymentToken);

	const [updateSubscriptionSettings, { loading: updateSubscriptionInProgress }] =
		useMutation(UPDATE_SUBSCRIPTION_SETTINGS);

	useEffect(() => {
		if (facilityInvoiceBillingProfiles && !loadedBillingSettings) {
			setBillingSettings([
				...billingSettings,
				...facilityInvoiceBillingProfiles.listManualInvoiceBillingSettings,
			]);
			setLoadedBillingSettings(true);
		}
	}, [billingSettings, facilityInvoiceBillingProfiles, loadedBillingSettings]);

	useEffect(() => {
		if (data && !queryError) {
			const subscription = data.getSubscriptionById;
			setManualInvoiceDueDateOffset(subscription.manualInvoiceDueDateOffset ?? 30);
			setAutoApplyCredits(subscription.autoApplyCredits);
			setAutoAccessEnabled(subscription.autoAccessEnabled ?? true);
			setAutoInvoiceSetting(subscription.autoInvoiceSetting);
			setBatchNewParkers(!!subscription.batchNewParkers);
			setSendBatchInvoiceOn(subscription.sendBatchInvoiceOn ?? '');
			setRecurringPaymentsSetting(subscription.recurringPaymentsSetting);
			setCreditCardFees(subscription.creditCardFees);
			setAutoPay(subscription.autoPay);
			setLateFees(subscription.lateFees);
			setMailInvoiceFees(subscription.mailInvoiceFees);
			setTransactionFees(subscription.transactionFees);
			setVehicleSurchargeFees(subscription.vehicleSurchargeFees);
			setNumberOfParkingSpaces(subscription.accessSystemMeta?.numberOfParkingSpaces);
			setTibaMType(subscription.accessSystemMeta?.tibaMType);
			setPoolSizeActive(subscription.accessSystemMeta?.poolSizeActive);
			setPoolSizeLimit(subscription.accessSystemMeta?.poolSizeLimit);
			setUnpaidInvoiceReminder(subscription.sendUnpaidInvoiceReminder);
			if (subscription.manualInvoiceBillingProfile) {
				setInvoiceOption(subscription.manualInvoiceBillingProfile.profileName);
				setSelectedBillingSetting(subscription.manualInvoiceBillingProfile);
			}
			setPageReady(true);
			setIntegration(subscription.billingProfile.facility.integration);
			setAllowACHPayments(!subscription.achDisabled);
			setAllowCreditCardPayments(subscription.allowCreditCardPayments);
		}
	}, [data, queryError]);

	const handleEdit = useCallback(() => {
		setOriginalValues({
			manualInvoiceDueDateOffset,
			allowACHPayments,
			autoApplyCredits,
			autoAccessEnabled,
			autoInvoiceSetting,
			batchNewParkers,
			sendBatchInvoiceOn,
			creditCardFees,
			lateFees,
			mailInvoiceFees,
			transactionFees,
			numberOfParkingSpaces,
			poolSizeActive,
			poolSizeLimit,
			sendUnpaidInvoiceReminder: unpaidInvoiceReminder,
			allowCreditCardPayments,
			tibaMType,
		});
		setEditMode(true);
	}, [
		manualInvoiceDueDateOffset,
		allowACHPayments,
		autoApplyCredits,
		autoAccessEnabled,
		autoInvoiceSetting,
		batchNewParkers,
		sendBatchInvoiceOn,
		creditCardFees,
		lateFees,
		mailInvoiceFees,
		transactionFees,
		numberOfParkingSpaces,
		poolSizeActive,
		poolSizeLimit,
		unpaidInvoiceReminder,
		allowCreditCardPayments,
		tibaMType,
	]);

	const handleSave = useCallback(async () => {
		const hasBatchInvoiceDay = sendBatchInvoiceOn !== '';

		if (batchNewParkers && !hasBatchInvoiceDay) {
			setShowSnackbar({
				message: 'Send True Up Invoice On is required when Batch New Parkers is enabled',
				severity: 'error',
			});
			return;
		}

		if (!batchNewParkers && hasBatchInvoiceDay) {
			setShowSnackbar({
				message: 'Batch New Parkers must be enabled when Send True Up Invoice On is selected',
				severity: 'error',
			});
			return;
		}

		if (batchNewParkers && autoInvoiceSetting !== 'AUTO_INVOICE') {
			setShowSnackbar({
				message: 'Batch New Parkers requires the Auto Invoice Setting to be set to Auto Invoice',
				severity: 'error',
			});
			return;
		}

		try {
			await updateSubscriptionSettings({
				variables: {
					facilityId,
					isFromTenantPortal: isTenantPortal,
					subscription: {
						autoApplyCredits,
						...(showAutoAccessControls && facilityAutoAccessEnabled && { autoAccessEnabled }),
						autoInvoiceSetting: AutoInvoiceSettingForPrismaEnum[autoInvoiceSetting],
						batchNewParkers,
						sendBatchInvoiceOn: hasBatchInvoiceDay ? Number(sendBatchInvoiceOn) : null,
						recurringPaymentsSetting,
						creditCardFees,
						lateFees,
						mailInvoiceFees,
						transactionFees,
						vehicleSurchargeFees,
						numberOfParkingSpaces,
						poolSizeActive,
						poolSizeLimit,
						sendUnpaidInvoiceReminder: unpaidInvoiceReminder,
						defaultInvoiceBillingProfileId: !selectedBillingSetting ? null : selectedBillingSetting.id,
						manualInvoiceDueDateOffset,
						achDisabled: facilityAllowsACHPayments ? !allowACHPayments : true,
						allowCreditCardPayments,
						tibaMType,
					},
					subscriptionId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});
			const originalInvoiceTemplateIds = subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.map(
				(d) => d.id,
			);
			const invoiceTemplateIds = [];
			if (monthlySelection && !originalInvoiceTemplateIds.includes(monthlySelection.id)) {
				invoiceTemplateIds.push(monthlySelection.id);
			}
			if (carryoverSelection && !originalInvoiceTemplateIds.includes(carryoverSelection.id)) {
				invoiceTemplateIds.push(carryoverSelection.id);
			}
			if (validationSelection && !originalInvoiceTemplateIds.includes(validationSelection.id)) {
				invoiceTemplateIds.push(validationSelection.id);
			}
			if (hotelStorageSelection && !originalInvoiceTemplateIds.includes(hotelStorageSelection.id)) {
				invoiceTemplateIds.push(hotelStorageSelection.id);
			}
			if (officeRetailSelection && !originalInvoiceTemplateIds.includes(officeRetailSelection.id)) {
				invoiceTemplateIds.push(officeRetailSelection.id);
			}
			if (miscSelection && !originalInvoiceTemplateIds.includes(miscSelection.id)) {
				invoiceTemplateIds.push(miscSelection.id);
			}
			if (invoiceTemplateIds.length > 0) {
				await createSubscriptionInvoiceTemplates({
					variables: {
						subscriptionId,
						invoiceTemplateIds,
						facilityId,
					},
				});
			}
			setShowSnackbar({
				message: 'Subscription settings updated',
				severity: 'success',
			});
			setEditMode(false);
		} catch (err) {
			setShowSnackbar({
				message: err.message,
				severity: 'error',
			});
		}
	}, [
		updateSubscriptionSettings,
		manualInvoiceDueDateOffset,
		showAutoAccessControls,
		facilityAutoAccessEnabled,
		facilityId,
		autoApplyCredits,
		autoAccessEnabled,
		autoInvoiceSetting,
		batchNewParkers,
		sendBatchInvoiceOn,
		recurringPaymentsSetting,
		creditCardFees,
		lateFees,
		mailInvoiceFees,
		transactionFees,
		numberOfParkingSpaces,
		unpaidInvoiceReminder,
		selectedBillingSetting,
		facilityAllowsACHPayments,
		allowACHPayments,
		subscriptionId,
		subscriptionInvoiceTemplateData?.getSubscriptionInvoiceTemplates,
		monthlySelection,
		carryoverSelection,
		validationSelection,
		hotelStorageSelection,
		officeRetailSelection,
		miscSelection,
		setShowSnackbar,
		createSubscriptionInvoiceTemplates,
		poolSizeActive,
		poolSizeLimit,
		allowCreditCardPayments,
		tibaMType,
		vehicleSurchargeFees,
	]);

	const handleChangeForInvoiceFormats = (e, setStateAction) => {
		const idToSearchFor = e.target.value;
		const templateToUse = allInvoiceTemplates?.getAllInvoiceTemplates.find((t) => t.id === idToSearchFor);
		setStateAction(templateToUse);
	};

	const setDefaultStates = useCallback(() => {
		setMiscSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Miscellaneous',
			),
		);
		setMonthlySelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Monthly',
			),
		);
		setCarryoverSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Carryover',
			),
		);
		setOfficeRetailSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Office Retail',
			),
		);
		setHotelStorageSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Hotel Storage',
			),
		);
		setValidationSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Validation',
			),
		);
	}, [subscriptionInvoiceTemplateData?.getSubscriptionInvoiceTemplates]);

	const handleCancel = useCallback(() => {
		setAutoApplyCredits(originalValues.autoApplyCredits);
		setManualInvoiceDueDateOffset(originalValues.manualInvoiceDueDateOffset);
		setAutoAccessEnabled(originalValues.autoAccessEnabled);
		setAutoInvoiceSetting(originalValues.autoInvoiceSetting);
		setBatchNewParkers(originalValues.batchNewParkers);
		setSendBatchInvoiceOn(originalValues.sendBatchInvoiceOn);
		setCreditCardFees(originalValues.creditCardFees);
		setLateFees(originalValues.lateFees);
		setMailInvoiceFees(originalValues.mailInvoiceFees);
		setTransactionFees(originalValues.transactionFees);
		setNumberOfParkingSpaces(originalValues.numberOfParkingSpaces);
		setTibaMType(originalValues.tibaMType);
		setUnpaidInvoiceReminder(originalValues.sendUnpaidInvoiceReminder);
		setAllowACHPayments(originalValues.allowACHPayments);
		setAllowCreditCardPayments(originalValues.allowCreditCardPayments);
		setDefaultStates();
		setEditMode(false);
	}, [originalValues, setDefaultStates]);

	useEffect(() => {
		if (
			!loadingSubscriptionInvoiceTemplates &&
			subscriptionInvoiceTemplateData &&
			!loadingAllInvoiceTemplates &&
			allInvoiceTemplates
		) {
			setDefaultStates();
			setInvoiceFormatDataLoaded(true);
		}
	}, [
		subscriptionInvoiceTemplateData,
		loadingSubscriptionInvoiceTemplates,
		invoiceFormatDataLoaded,
		loadingAllInvoiceTemplates,
		allInvoiceTemplates,
		setDefaultStates,
	]);

	const onTooltipOpen = useCallback(() => {
		if (editMode && !facilityAllowsCreditCardPayments) {
			setShowCreditCardPaymentTooltip(true);
		}
	}, [editMode, facilityAllowsCreditCardPayments, setShowCreditCardPaymentTooltip]);

	if (!pageReady) {
		if (queryError) {
			console.log(queryError);
		}

		return (
			<Grid container direction='column' alignItems='center' sx={{ marginTop: '75px' }}>
				{queryLoading ? <CircularProgress /> : <strong style={{ color: 'red' }}>Error loading page</strong>}
			</Grid>
		);
	}

	const billingSettingsOnChange = (e) => {
		const selected = billingSettings.find(({ profileName }) => profileName === e.target.value);
		setInvoiceOption(e.target.value);
		setSelectedBillingSetting(selected.billingDayInArrears === 0 ? null : selected);
	};

	const isBankAccountBeingUsedForRecurringPayments = autoPay && isBankAccountSelectedForRecurringPayments;

	return (
		<Box sx={{ py: 3, width: '100%', boxSizing: 'border-box' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					mb: 2.5,
					flexWrap: 'wrap',
				}}
			>
				<Box>
					<Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
						Subscription Settings
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Manage billing, payments, and facility access for this subscription.
					</Typography>
				</Box>
				{!isOwnerPortal && (
					<Box>
						{editMode && (
							<>
								<Button
									variant='contained'
									onClick={handleSave}
									sx={{ marginRight: '10px' }}
									data-cy='save-button'
								>
									{updateSubscriptionInProgress || createSubscriptionInvoiceInProgress ? (
										<CircularProgress
											size={25}
											sx={{ color: 'white' }}
											data-cy='loading-indicator'
										/>
									) : (
										'Save'
									)}
								</Button>
								{!updateSubscriptionInProgress && !createSubscriptionInvoiceInProgress && (
									<Button
										sx={{ border: '1px solid' }}
										color='error'
										onClick={handleCancel}
										data-cy='cancel-button'
									>
										Cancel
									</Button>
								)}
							</>
						)}
						{!editMode && canEditSubscriptionSettings && (
							<Button disabled={loading} variant='contained' onClick={handleEdit} data-cy='edit-button'>
								Edit
							</Button>
						)}
					</Box>
				)}
			</Box>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
					gap: 3,
					alignItems: 'start',
				}}
			>
				<SubscriptionSettingsSection title='Invoicing'>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Auto Invoice Setting</strong>
						</Box>
						{editMode ? (
							<Box>
								<Select
									value={autoInvoiceSetting}
									onChange={(e) => setAutoInvoiceSetting(e.target.value)}
									data-cy='auto-invoice-setting-select-menu'
								>
									{Object.keys(AutoInvoiceSettingEnum).map((key, i) => (
										<MenuItem value={key} key={i}>
											{AutoInvoiceSettingEnum[key]}
										</MenuItem>
									))}
								</Select>
							</Box>
						) : (
							<span
								style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
								data-cy='auto-invoice-setting-text'
							>
								{AutoInvoiceSettingEnum[autoInvoiceSetting]}
							</span>
						)}
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Validation Bill Backs Billing Setting</strong>
						</Box>
						<Box>
							<Select
								value={invoiceOption}
								required
								disabled={!editMode}
								onChange={billingSettingsOnChange}
							>
								{billingSettings.map((type: any, index) => (
									<MenuItem disabled={type.profileName === '-'} key={index} value={type.profileName}>
										{type.profileName}
									</MenuItem>
								))}
							</Select>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Manual Invoice Due Date Offset (Days)</strong>
						</Box>
						<Box>
							<Select
								value={manualInvoiceDueDateOffset}
								disabled={!editMode}
								inputProps={{ 'aria-label': 'Manual Invoice Due Date Offset (Days)' }}
								onChange={(event) => setManualInvoiceDueDateOffset(Number(event.target.value))}
							>
								{Array.from({ length: 61 }, (_, days) => (
									<MenuItem key={days} value={days}>
										{days}
									</MenuItem>
								))}
							</Select>
							<Typography variant='caption' display='block'>
								Days from today for Manual Invoice Default Due Date. Does not affect Daily Report
								Validation Bill Backs.
							</Typography>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>
								Batch New Parkers
								<Chip label='True Up' size='small' sx={{ marginLeft: 1, height: 20 }} />
								<Tooltip
									className='info-icon'
									title='When using True Up Invoice, the subscription must have the Auto Invoice Setting set to "Auto Invoice". This setting is located at the top of this page.'
									placement='right'
									arrow
									style={{ zIndex: 9999 }}
								>
									<InfoIcon fontSize='small' />
								</Tooltip>
							</strong>
						</Box>
						<Box>
							<Switch
								checked={batchNewParkers}
								onChange={(e) => {
									const checked = e.target.checked;
									setBatchNewParkers(checked);
									if (!checked) {
										setSendBatchInvoiceOn('');
									}
								}}
								disabled={!editMode}
								data-cy='batch-new-parkers-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>
								Send True Up Invoice On
								<Chip label='True Up' size='small' sx={{ marginLeft: 1, height: 20 }} />
								<Tooltip
									className='info-icon'
									title='When using True Up Invoice, the subscription must have the Auto Invoice Setting set to "Auto Invoice". This setting is located at the top of this page.'
									placement='right'
									arrow
									style={{ zIndex: 9999 }}
								>
									<InfoIcon fontSize='small' />
								</Tooltip>
							</strong>
						</Box>
						{editMode ? (
							<Box>
								<Select
									value={sendBatchInvoiceOn}
									onChange={(e) =>
										setSendBatchInvoiceOn(e.target.value === '' ? '' : Number(e.target.value))
									}
									displayEmpty
									disabled={!batchNewParkers}
									data-cy='send-batch-invoice-on-select-menu'
								>
									<MenuItem value=''>Select day</MenuItem>
									{BATCH_INVOICE_DAY_OPTIONS.map((day) => (
										<MenuItem key={day} value={day}>
											{day}
										</MenuItem>
									))}
								</Select>
							</Box>
						) : (
							<span
								style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
								data-cy='send-batch-invoice-on-text'
							>
								{batchNewParkers && sendBatchInvoiceOn
									? `${getOrdinalSuffix(Number(sendBatchInvoiceOn))} day of the month`
									: '—'}
							</span>
						)}
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Send Unpaid Invoice Reminder</strong>
						</Box>
						<Box>
							<Switch
								checked={unpaidInvoiceReminder}
								onChange={(e) => setUnpaidInvoiceReminder(e.target.checked)}
								disabled={!editMode}
								data-cy='unpaid-invoice-reminder-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
				</SubscriptionSettingsSection>
				<SubscriptionSettingsSection title='Payments'>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Recurring Payments Setting</strong>
						</Box>
						{editMode ? (
							<Box>
								<Select
									value={recurringPaymentsSetting}
									onChange={(e) => setRecurringPaymentsSetting(e.target.value)}
									data-cy='recurring-payments-setting-select-menu'
								>
									{Object.keys(RecurringPaymentsSettingDisplayEnum).map((key, i) => (
										<MenuItem value={key} key={i}>
											{RecurringPaymentsSettingDisplayEnum[key]}
										</MenuItem>
									))}
								</Select>
							</Box>
						) : (
							<span
								style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
								data-cy='recurring-payments-setting-text'
							>
								{RecurringPaymentsSettingDisplayEnum[recurringPaymentsSetting]}
							</span>
						)}
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Auto-Apply Credits</strong>
						</Box>
						<Box>
							<Switch
								checked={autoApplyCredits}
								onChange={(e) => setAutoApplyCredits(e.target.checked)}
								disabled={!editMode}
								data-cy='auto-apply-credits-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Allow Credit Card Payments</strong>
						</Box>
						<Box>
							<Tooltip
								title='Facility does not allow credit card payments'
								placement='right'
								open={showCreditCardPaymentTooltip}
								onOpen={onTooltipOpen}
								arrow
							>
								<Switch
									checked={allowCreditCardPayments}
									onChange={(e) => setAllowCreditCardPayments(e.target.checked)}
									disabled={!editMode || !facilityAllowsCreditCardPayments}
									data-cy='auto-apply-credits-switch'
								/>
							</Tooltip>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Allow ACH Payments</strong>
						</Box>
						<Box>
							<Tooltip
								title={
									isBankAccountBeingUsedForRecurringPayments
										? `
                                ACH Payments cannot be disabled while
                                recurring payments are enabled with a bank account
                            `
										: 'Facility does not allow ACH Payments'
								}
								placement='right'
								open={showACHPaymentTooltip}
								arrow
							>
								<span
									onMouseEnter={() => {
										if (
											editMode &&
											(!facilityAllowsACHPayments || isBankAccountBeingUsedForRecurringPayments)
										) {
											setShowACHPaymentTooltip(true);
										}
									}}
									onMouseLeave={() => setShowACHPaymentTooltip(false)}
								>
									<Switch
										checked={allowACHPayments ?? true}
										onChange={(e) => setAllowACHPayments(e.target.checked)}
										disabled={
											!editMode ||
											!facilityAllowsACHPayments ||
											isBankAccountBeingUsedForRecurringPayments
										}
										data-cy='allow-ach-payments-switch'
									/>
								</span>
							</Tooltip>
						</Box>
					</SubscriptionSettingsRow>
				</SubscriptionSettingsSection>
				<SubscriptionSettingsSection title='Fees'>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Credit Card Fees</strong>
						</Box>
						<Box>
							<Switch
								checked={creditCardFees}
								onChange={(e) => setCreditCardFees(e.target.checked)}
								disabled={!editMode}
								data-cy='credit-card-fees-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Late Fees</strong>
						</Box>
						<Box>
							<Switch
								checked={lateFees}
								onChange={(e) => setLateFees(e.target.checked)}
								disabled={!editMode}
								data-cy='late-fees-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Mail Invoice Fees</strong>
						</Box>
						<Box>
							<Switch
								checked={mailInvoiceFees}
								onChange={(e) => setMailInvoiceFees(e.target.checked)}
								disabled={!editMode}
								data-cy='mail-invoice-fees-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Transaction Fees</strong>
						</Box>
						<Box>
							<Switch
								checked={transactionFees}
								onChange={(e) => setTransactionFees(e.target.checked)}
								disabled={!editMode}
								data-cy='transaction-fees-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
					<SubscriptionSettingsRow>
						<Box>
							<strong>Vehicle Surcharge Fees</strong>
						</Box>
						<Box>
							<Switch
								checked={vehicleSurchargeFees}
								onChange={(e) => setVehicleSurchargeFees(e.target.checked)}
								disabled={!editMode}
								data-cy='vehicle-surcharge-fees-switch'
							/>
						</Box>
					</SubscriptionSettingsRow>
				</SubscriptionSettingsSection>
				{(showAutoAccessControls || integration === 'TIBA' || integration === 'PARKONECT') && (
					<SubscriptionSettingsSection title='Access Controls'>
						{showAutoAccessControls && (
							<SubscriptionAutoAccessControl
								facilityEnabled={facilityAutoAccessEnabled}
								enabled={autoAccessEnabled}
								editMode={editMode}
								onChange={setAutoAccessEnabled}
							/>
						)}
						{integration === 'TIBA' && (
							<>
								<SubscriptionSettingsRow>
									<Box>
										<strong>
											Integration Settings - Number of Parking Spaces <br /> (Tiba Only)
										</strong>
									</Box>
									<Box>
										<TextField
											id='num-parking-spaces'
											name='num-parking-spaces'
											disabled={!editMode}
											value={numberOfParkingSpaces || null}
											onChange={(e) => setNumberOfParkingSpaces(Number(e.target.value))}
											type='number'
											inputProps={{ 'data-cy': 'number-of-parking-spaces-input' }}
										/>
									</Box>
								</SubscriptionSettingsRow>
								<SubscriptionSettingsRow>
									<Box>
										<strong>
											Integration Settings - TIBA Active mType <br />
										</strong>
									</Box>

									<Box>
										<TextField
											select
											fullWidth
											value={tibaMType ?? ''}
											label='Tiba Active mType'
											onChange={(e) => setTibaMType(Number(e.target.value))}
											disabled={!editMode}
										>
											<MenuItem value={2}>Standard</MenuItem>
											<MenuItem value={8}>Resider</MenuItem>
											<MenuItem value={4}>Master</MenuItem>
											<MenuItem value={1}>Units</MenuItem>
										</TextField>
									</Box>
								</SubscriptionSettingsRow>
							</>
						)}
						{integration === 'PARKONECT' && (
							<>
								<SubscriptionSettingsRow>
									<Box>
										<strong>
											Integration Settings - Pool Size Active <br /> (Parkonect Only)
										</strong>
									</Box>
									<Box>
										<Switch
											checked={poolSizeActive}
											onChange={(e) => setPoolSizeActive(e.target.checked)}
											disabled={!editMode}
											data-cy='pool-size-active-switch'
										/>
									</Box>
								</SubscriptionSettingsRow>
								<SubscriptionSettingsRow>
									<Box>
										<strong>
											Integration Settings - Pool Size Limit
											<br /> (Parkonect Only)
										</strong>
									</Box>
									<Box>
										<TextField
											id='pool-size-limit'
											name='pool-size-limit'
											disabled={!editMode}
											value={poolSizeLimit || null}
											onChange={(e) => setPoolSizeLimit(Number(e.target.value))}
											type='number'
											inputProps={{ 'data-cy': 'pool-size-limit-input' }}
										/>
									</Box>
								</SubscriptionSettingsRow>
							</>
						)}
					</SubscriptionSettingsSection>
				)}
				<SubscriptionSettingsSection title='Invoice Formats' fullWidth>
					<Typography variant='body2' color='text.secondary' sx={{ pt: 2, pb: 3 }}>
						Subscription formats override the facility and organization defaults.
					</Typography>
					<Box
						sx={{
							pb: 2.5,
							'& > .MuiGrid-container': {
								display: 'grid',
								gridTemplateColumns: {
									xs: 'minmax(0, 1fr)',
									sm: 'repeat(2, minmax(0, 1fr))',
									lg: 'repeat(3, minmax(0, 1fr))',
								},
								gap: 2,
								width: '100%',
								m: 0,
							},
							'& > .MuiGrid-container > .MuiGrid-item': {
								width: '100%',
								maxWidth: '100%',
								minWidth: 0,
								p: 0,
							},
							'& .invoice-format-tab-loading': { height: '160px !important' },
						}}
					>
						<InvoiceTemplateSelections
							allInvoiceTemplates={allInvoiceTemplates?.getAllInvoiceTemplates}
							handleChange={handleChangeForInvoiceFormats}
							monthlySelection={monthlySelection}
							carryoverSelection={carryoverSelection}
							validationSelection={validationSelection}
							hotelStorageSelection={hotelStorageSelection}
							officeRetailSelection={officeRetailSelection}
							miscSelection={miscSelection}
							setMonthlySelection={setMonthlySelection}
							setCarryoverSelection={setCarryoverSelection}
							setValidationSelection={setValidationSelection}
							setHotelStorageSelection={setHotelStorageSelection}
							setOfficeRetailSelection={setOfficeRetailSelection}
							setMiscSelection={setMiscSelection}
							editMode={editMode}
							dataLoaded={invoiceFormatDataLoaded}
						/>
					</Box>
				</SubscriptionSettingsSection>
			</Box>
		</Box>
	);
};
